import { Link } from 'react-router-dom';
import {
  Truck, Zap, Shield, Star, RefreshCw, Wrench,
  FileText, MapPin, ChevronRight, CheckCircle, Users,
  PoundSterling, ArrowRight, Package, TrendingUp, Award,
  Lock, ScrollText, MapPinned,
} from 'lucide-react';
import Footer from '../components/Footer';

export function HomePage() {
  return (
    <div className="min-h-screen bg-white">

      {/* ── Hero ── */}
      <section className="relative bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 text-white px-4 pt-24 pb-28 overflow-hidden">
        {/* Decorative blobs */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-emerald-500/10 blur-3xl" />
          <div className="absolute top-40 -left-24 w-80 h-80 rounded-full bg-green-400/8 blur-3xl" />
          <div className="absolute bottom-0 right-1/3 w-96 h-40 rounded-full bg-teal-500/10 blur-2xl" />
          {/* Grid overlay */}
          <div className="absolute inset-0 opacity-[0.03]"
            style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        </div>

        <div className="relative max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-400/30
                          rounded-full px-4 py-1.5 text-sm mb-8 backdrop-blur-sm text-emerald-200">
            <Zap className="w-3.5 h-3.5 text-amber-400" />
            <span>Launching Luton — July 2026</span>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse ml-1" />
          </div>

          <h1 className="text-5xl md:text-7xl font-extrabold mb-6 leading-[1.08] tracking-tight">
            The UK's{' '}
            <br />Transport Marketplace
          </h1>

          <p className="text-slate-300 text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            Same-day delivery, long-distance relay routes, and vehicle repair bidding —
            all in one platform. <strong className="text-white">No membership fees. UK-based, UK data only.</strong>
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-14">
            <Link to="/register"
              className="bg-emerald-500 hover:bg-emerald-400 text-white font-bold px-10 py-4 rounded-2xl
                         transition-all shadow-lg shadow-emerald-900/50 text-base flex items-center justify-center gap-2">
              Get Started — It's Free <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/jobs"
              className="bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold
                         px-10 py-4 rounded-2xl transition-all backdrop-blur-sm text-base">
              Browse Jobs
            </Link>
          </div>

          {/* Social proof bar */}
          <div className="flex flex-wrap justify-center gap-8 text-sm text-slate-400">
            {[
              { value: '0%', label: 'Membership fee' },
              { value: 'UK-only', label: 'Data storage & hosting' },
              { value: '5%', label: 'Cashback on first 3 orders' },
            ].map(s => (
              <div key={s.label} className="text-center">
                <p className="text-2xl font-extrabold text-white">{s.value}</p>
                <p className="text-xs mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── USP strip ── */}
      <section className="border-b border-gray-100 bg-white shadow-sm">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-100">
            {[
              { icon: <Lock className="w-5 h-5 text-emerald-600" />, label: 'UK Data Only',     sub: 'Hosted & retained in the UK' },
              { icon: <Shield className="w-5 h-5 text-blue-600" />,   label: 'No Hidden Fees',   sub: 'Pay nothing until job is complete' },
              { icon: <RefreshCw className="w-5 h-5 text-violet-600" />, label: 'Relay Model',   sub: 'Reduce dead miles across UK' },
              { icon: <FileText className="w-5 h-5 text-amber-600" />, label: 'Auto Contracts',  sub: 'PDF generated on acceptance' },
            ].map(item => (
              <div key={item.label} className="py-6 px-4 text-center">
                <div className="flex justify-center mb-2">{item.icon}</div>
                <p className="font-semibold text-gray-900 text-sm">{item.label}</p>
                <p className="text-gray-400 text-xs mt-0.5">{item.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Who it's for (3 role cards) ── */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-emerald-600 text-sm font-semibold uppercase tracking-widest">The Platform</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-3">Built for everyone in the supply chain</h2>
            <p className="text-gray-500 mt-3 max-w-xl mx-auto">One platform. Three user types. All the tools you need to move goods efficiently across the UK.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <RoleCard
              gradient="from-emerald-500 to-teal-600"
              icon={<Truck className="w-7 h-7 text-white" />}
              title="Drivers & Hauliers"
              tagline="Earn more. Drive smarter."
              points={[
                'Bid on jobs that fit your existing route',
                'Free DVLA & insurance verification',
                'Relay segments reduce dead miles',
                'Post your vehicle for repair bids',
                '1% referral income for new carriers',
              ]}
              cta="Start Driving"
              href="/register"
            />
            <RoleCard
              gradient="from-blue-500 to-indigo-600"
              icon={<Package className="w-7 h-7 text-white" />}
              title="Customers"
              tagline="Ship anything. Anywhere."
              points={[
                'Post a job in 60 seconds',
                'Competitive bids from verified carriers',
                'Legal PDF contract auto-generated',
                '5% cashback on first 3 deliveries',
                'Real-time tracking across relay legs',
              ]}
              cta="Post a Job"
              href="/post-job"
            />
            <RoleCard
              gradient="from-amber-500 to-orange-600"
              icon={<Wrench className="w-7 h-7 text-white" />}
              title="Workshops"
              tagline="Win repair work. No cold calling."
              points={[
                'Receive repair requests from verified carriers',
                'Bid competitively on vehicle jobs',
                'Free rated workshop profile listing',
                'Payments processed securely through the platform',
                'Offer exclusive discount codes to carriers',
              ]}
              cta="Join as Workshop"
              href="/register"
            />
          </div>
        </div>
      </section>

      {/* ── For Drivers — detail ── */}
      <section className="max-w-5xl mx-auto px-4 py-20">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-emerald-600 text-sm font-semibold uppercase tracking-widest">For Carriers</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-3 mb-5 leading-tight">
              More jobs.<br />Less dead miles.<br />Faster payment.
            </h2>
            <div className="space-y-3.5">
              {[
                'Bid on jobs that fit your existing route — relay segments reduce empty miles significantly',
                'Free legal contract auto-generated on every accepted job',
                'No membership fees ever — free to join, free to bid',
                'Post your vehicle for repair and receive competitive bids from vetted workshops',
                'Earn 1% referral income for every driver you bring to the platform',
                'AI chatbot builds your profile through conversation — no long registration forms',
              ].map(pt => (
                <div key={pt} className="flex items-start gap-3 text-sm text-gray-700">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                  {pt}
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-8">
              <Link to="/jobs" className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-3 rounded-xl transition-colors text-sm">
                View Available Jobs <ChevronRight className="w-4 h-4" />
              </Link>
              <Link to="/register" className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-6 py-3 rounded-xl transition-colors text-sm">
                Create Account
              </Link>
            </div>
          </div>

          {/* Free services preview */}
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-emerald-100 to-teal-50 rounded-3xl -z-10" />
            <div className="bg-white rounded-2xl shadow-xl border border-emerald-100 overflow-hidden">
              <div className="px-5 py-4 bg-gradient-to-r from-emerald-600 to-teal-600">
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-emerald-100" />
                  <p className="text-sm font-semibold text-white">Free for Carriers</p>
                </div>
                <p className="text-emerald-200 text-xs mt-0.5">Included at no cost, always</p>
              </div>
              <div className="p-4 space-y-3">
                <FreeServiceRow icon={<Shield className="w-4 h-4 text-emerald-600" />} label="DVLA & insurance verification" />
                <FreeServiceRow icon={<FileText className="w-4 h-4 text-emerald-600" />} label="Auto-generated legal contracts" />
                <FreeServiceRow icon={<MapPinned className="w-4 h-4 text-emerald-600" />} label="Relay route matching" />
                <FreeServiceRow icon={<Users className="w-4 h-4 text-emerald-600" />} label="Dispute mediation support" />
              </div>
              <div className="px-4 py-3 bg-emerald-50 border-t border-emerald-100">
                <p className="text-xs text-emerald-700 font-medium">
                  Paid directly to your bank once your POD is verified.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── How it works for customers ── */}
      <section className="bg-slate-900 py-20">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-blue-400 text-sm font-semibold uppercase tracking-widest">For Customers</span>
            <h2 className="text-4xl font-bold text-white mt-3">Post a job. Get bids. Ship with confidence.</h2>
            <p className="text-slate-400 mt-3 max-w-xl mx-auto">
              Free to post. Verified drivers. Legal contract auto-generated. 5% cashback on your first three orders.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {[
              { step: '01', title: 'Describe your job', icon: <MapPin className="w-6 h-6 text-blue-400" />,
                desc: 'Use our AI chatbot or simple form. Collection point, delivery address, goods type, date. Done in 60 seconds.' },
              { step: '02', title: 'Receive competitive bids', icon: <Star className="w-6 h-6 text-amber-400" />,
                desc: 'Verified, rated drivers place bids. Compare vehicle type, driver rating, and net price. No hidden charges.' },
              { step: '03', title: 'Legal contract generated', icon: <FileText className="w-6 h-6 text-emerald-400" />,
                desc: 'Accept a quote → an instant PDF contract is emailed to you and the driver. Your goods are protected.' },
            ].map(item => (
              <div key={item.step} className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 hover:border-slate-600 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl font-extrabold text-slate-600 font-mono">{item.step}</span>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  {item.icon}
                  <h3 className="font-semibold text-white">{item.title}</h3>
                </div>
                <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/post-job"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-bold px-10 py-4 rounded-2xl transition-colors shadow-lg">
              Post Your First Job — Free <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Relay model ── */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-1.5 bg-violet-100 text-violet-700 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-4">
              <Zap className="w-3.5 h-3.5" /> TransitDeal Exclusive
            </span>
            <h2 className="text-4xl font-bold text-gray-900">The Relay Model</h2>
            <p className="text-gray-500 mt-3 max-w-2xl mx-auto">
              Long-distance jobs split into segments. Each driver takes the leg that matches their natural route.
              Every driver earns. Customer pays one price. Zero dead miles.
            </p>
          </div>

          <div className="bg-gradient-to-br from-violet-50 via-purple-50 to-blue-50 border border-violet-100 rounded-3xl p-8">
            {/* Route visual */}
            <div className="flex items-center gap-1 mb-8 overflow-x-auto">
              {[
                { city: 'Luton', icon: '🚦' },
                { city: 'Leicester', icon: '' },
                { city: 'Sheffield', icon: '' },
                { city: 'Leeds', icon: '📦' },
              ].map((node, i, arr) => (
                <div key={node.city} className="flex items-center gap-1 shrink-0">
                  <div className="text-center">
                    <div className="text-lg mb-0.5">{node.icon}</div>
                    <div className="text-xs font-semibold text-gray-700">{node.city}</div>
                  </div>
                  {i < arr.length - 1 && (
                    <div className="flex-1 min-w-12 h-0.5 bg-gradient-to-r from-violet-400 to-purple-400 mx-2 relative">
                      <div className="absolute -top-1 right-1/2">
                        <ChevronRight className="w-3 h-3 text-violet-500" />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              {[
                { route: 'Luton → Leicester',    miles: '56 miles', fee: '£4.20', carrier: 'Carrier A — Sprinter Van',  color: 'violet' },
                { route: 'Leicester → Sheffield', miles: '62 miles', fee: '£4.65', carrier: 'Carrier B — 7.5t HGV',     color: 'purple' },
                { route: 'Sheffield → Leeds',     miles: '50 miles', fee: '£3.75', carrier: 'Carrier C — Transit Van',  color: 'blue' },
              ].map((seg, i) => (
                <div key={i} className="bg-white rounded-2xl p-5 shadow-sm border border-violet-100">
                  <div className="text-violet-600 text-xs font-bold uppercase tracking-wider mb-2">Segment {i + 1}</div>
                  <div className="font-bold text-gray-900 mb-1">{seg.route}</div>
                  <div className="text-gray-400 text-xs mb-3">{seg.miles}</div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{seg.carrier}</span>
                    <span className="text-lg font-extrabold text-emerald-600">{seg.fee}</span>
                  </div>
                  <div className="text-xs text-gray-400 text-right mt-0.5">relay fee</div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 pt-5 border-t border-violet-200">
              <p className="text-sm text-violet-700 font-medium">
                Customer pays <strong>one price, one contract</strong>. Real-time tracking across all relay legs.
              </p>
              <Link to="/post-job" className="shrink-0 text-sm font-semibold text-violet-700 hover:text-violet-900 flex items-center gap-1 transition-colors">
                Post a relay job <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Free services ── */}
      <section className="py-20 bg-white border-t border-gray-100">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-emerald-600 text-sm font-semibold uppercase tracking-widest">Always Free</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-3">Services included at no cost</h2>
            <p className="text-gray-500 mt-3 max-w-xl mx-auto">
              No subscriptions, no listing fees, no catches — these are free for every user, every time.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-5">
            <FreeServiceCard
              icon={<Truck className="w-6 h-6 text-emerald-600" />}
              title="Carriers"
              items={[
                'Post & bid on jobs',
                'DVLA & insurance checks',
                'Auto-generated contracts',
                'Rated profile',
              ]}
            />
            <FreeServiceCard
              icon={<Package className="w-6 h-6 text-blue-600" />}
              title="Customers"
              items={[
                'Post a job',
                'Compare bids',
                'Legal contract on acceptance',
                'Dispute support',
              ]}
            />
            <FreeServiceCard
              icon={<Wrench className="w-6 h-6 text-amber-600" />}
              title="Workshops"
              items={[
                'List your workshop',
                'Receive repair requests',
                'Rated profile',
                'Discount code promotion',
              ]}
            />
            <FreeServiceCard
              icon={<Package className="w-6 h-6 text-violet-600" />}
              title="Bazaar buyers & sellers"
              items={[
                'List an item for sale',
                'Identity verification',
                'Buyer–seller chat',
                'Secure checkout',
              ]}
            />
          </div>
        </div>
      </section>

      {/* ── Security & trust ── */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-emerald-400 text-sm font-semibold uppercase tracking-widest">Security & Trust</span>
            <h2 className="text-4xl font-bold text-white mt-3">Built to UK standards</h2>
            <p className="text-slate-400 mt-3 max-w-xl mx-auto">
              A UK company, hosting UK data, protecting every job with verification and mediation.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-5">
            {[
              { icon: <Lock className="w-5 h-5 text-emerald-400" />, title: 'UK Data Residency', desc: 'All user data is hosted and retained in the UK.' },
              { icon: <Shield className="w-5 h-5 text-emerald-400" />, title: 'Verified Carriers', desc: 'DVLA licence and insurance checks before approval.' },
              { icon: <ScrollText className="w-5 h-5 text-emerald-400" />, title: 'Legal Contracts', desc: 'A binding PDF contract is generated for every job.' },
              { icon: <Users className="w-5 h-5 text-emerald-400" />, title: 'Dispute Mediation', desc: 'Admin-reviewed resolution if something goes wrong.' },
            ].map(s => (
              <div key={s.title} className="bg-slate-800/50 border border-slate-700 rounded-2xl p-5">
                <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center mb-3">{s.icon}</div>
                <p className="font-semibold text-white text-sm">{s.title}</p>
                <p className="text-slate-400 text-xs mt-1 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials placeholder ── */}
      <section className="py-20 bg-gray-50 border-t border-gray-100">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-amber-600 text-sm font-semibold uppercase tracking-widest">Early Feedback</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-3">What our beta users say</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { name: 'James W.',    role: 'Owner-driver, Luton',
                quote: 'Finally got paid the same week I delivered. Used to wait 45 days with my old agency. TransitDeal is a game changer.' },
              { name: 'Sarah T.',   role: 'Online retailer, Milton Keynes',
                quote: 'Posted my first job and had 6 bids in under an hour. The auto-contract feature saved me from my solicitor\'s rates!' },
              { name: 'Mike\'s Workshop', role: 'Commercial repair, Dunstable',
                quote: 'We\'re already getting HGV repair requests from drivers who found us through the platform. Brilliant for local trade.' },
            ].map(t => (
              <div key={t.name} className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-gray-700 text-sm leading-relaxed mb-5 italic">"{t.quote}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white font-bold text-sm">
                    {t.name[0]}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{t.name}</p>
                    <p className="text-xs text-gray-400">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Referral / community ── */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-3xl p-10 text-white text-center relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none" aria-hidden>
              <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/5 blur-2xl" />
              <div className="absolute bottom-0 -left-8 w-48 h-48 rounded-full bg-white/5 blur-xl" />
            </div>
            <div className="relative">
              <div className="inline-flex items-center gap-2 bg-white/15 border border-white/20 rounded-full px-4 py-1.5 text-sm mb-5">
                <Users className="w-3.5 h-3.5" /> Refer & Earn
              </div>
              <h2 className="text-4xl font-bold mb-4">Earn 1% on every job your referrals complete</h2>
              <p className="text-emerald-100 max-w-xl mx-auto mb-8 text-lg">
                Invite a driver. They earn. <strong className="text-white">You earn 1% of their gross job value — indefinitely.</strong> No cap. No expiry.
              </p>
              <div className="grid sm:grid-cols-3 gap-4 max-w-2xl mx-auto mb-8">
                {[
                  { icon: <Award className="w-5 h-5" />, label: '1% per job', sub: 'From every job your referral completes' },
                  { icon: <PoundSterling className="w-5 h-5" />, label: 'No cap', sub: 'Referral earnings are unlimited' },
                  { icon: <TrendingUp className="w-5 h-5" />, label: 'Passive income', sub: 'Earn while you sleep' },
                ].map(s => (
                  <div key={s.label} className="bg-white/10 border border-white/20 rounded-xl p-4">
                    <div className="flex justify-center mb-2 text-emerald-200">{s.icon}</div>
                    <p className="font-bold text-sm">{s.label}</p>
                    <p className="text-xs text-emerald-200 mt-0.5">{s.sub}</p>
                  </div>
                ))}
              </div>
              <Link to="/register"
                className="inline-flex items-center gap-2 bg-white text-emerald-700 font-bold px-8 py-4 rounded-2xl hover:bg-emerald-50 transition-colors shadow-lg">
                Join & Get Your Referral Code <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-5xl font-extrabold text-white mb-5 leading-tight">
            Ready to move<br />
            <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
              smarter?
            </span>
          </h2>
          <p className="text-slate-400 text-xl mb-10">
            Join the waitlist now. Free to sign up. No commitment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register"
              className="bg-emerald-500 hover:bg-emerald-400 text-white font-bold px-10 py-4 rounded-2xl transition-all shadow-lg shadow-emerald-900/40 text-base">
              Create Free Account
            </Link>
            <Link to="/jobs"
              className="bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold px-10 py-4 rounded-2xl transition-all text-base">
              Browse Jobs First
            </Link>
          </div>
          <p className="text-slate-500 text-xs mt-6">
            No credit card required · No membership fee · Cancel anytime
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

interface RoleCardProps {
  gradient: string;
  icon: React.ReactNode;
  title: string;
  tagline: string;
  points: string[];
  cta: string;
  href: string;
}

function RoleCard({ gradient, icon, title, tagline, points, cta, href }: RoleCardProps) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col">
      <div className={`bg-gradient-to-br ${gradient} px-6 py-6`}>
        <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center mb-3">
          {icon}
        </div>
        <h3 className="text-white font-bold text-lg">{title}</h3>
        <p className="text-white/70 text-sm mt-0.5">{tagline}</p>
      </div>
      <div className="px-6 py-5 flex-1 flex flex-col">
        <ul className="space-y-2.5 flex-1">
          {points.map(pt => (
            <li key={pt} className="flex items-start gap-2 text-sm text-gray-600">
              <CheckCircle className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
              {pt}
            </li>
          ))}
        </ul>
        <Link to={href}
          className="mt-5 w-full text-center bg-gray-900 hover:bg-gray-700 text-white font-semibold py-2.5 rounded-xl text-sm transition-colors flex items-center justify-center gap-1.5">
          {cta} <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

function FreeServiceCard({ icon, title, items }: { icon: React.ReactNode; title: string; items: string[] }) {
  return (
    <div className="bg-gray-50 border border-gray-100 rounded-2xl p-5">
      <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center mb-3">{icon}</div>
      <p className="font-bold text-gray-900 mb-2.5">{title}</p>
      <ul className="space-y-1.5">
        {items.map(item => (
          <li key={item} className="flex items-start gap-1.5 text-xs text-gray-600">
            <CheckCircle className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function FreeServiceRow({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="bg-gray-50 rounded-xl p-3.5 border border-gray-100 flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">{icon}</div>
      <p className="text-sm text-gray-700 font-medium">{label}</p>
      <span className="ml-auto text-xs font-bold text-emerald-600 uppercase tracking-wide">Free</span>
    </div>
  );
}
